import { useEffect, useState } from "react";
import { Receipt, Save, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getPayoutSettings, updatePayoutSettings } from "@/lib/api";

const FIELDS = [
  ["discount_approval_pct", "Manager approval needed when discount exceeds (% of balance)", "number"],
  ["valid_until_time", "Default valid-until time", "time"],
  ["signatory_title", "Signatory title on the letter", "text"],
  ["sender_phone_uk", "UK accounts phone (letter footer)", "text"],
  ["sender_phone_aus", "AU accounts phone (letter footer)", "text"],
];

export function PayoutLetterSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getPayoutSettings().then(setS).catch(() => toast.error("Couldn't load payout settings")); }, []);
  const save = async () => {
    setBusy(true);
    try { setS(await updatePayoutSettings({ ...s, discount_approval_pct: Number(s.discount_approval_pct) })); toast.success("Payout letter settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="payout-settings-card">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="w-4 h-4" />Payout / settlement letters</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">Templates live in DocuPilot (UK 111072 · AU 111071). Letters are emailed from accounts@simplyfunded.co.uk / accounts@simplyfunded.com.au and filed to the customer's OneDrive folder.</p>
        <div className="rounded-md border border-dashed p-3 text-xs space-y-1.5" data-testid="payout-template-downloads">
          <div className="font-medium flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" />DocuPilot-ready template files</div>
          <p className="text-muted-foreground">Open the template in DocuPilot → <b>Replace document</b> and upload the matching file (IDs stay the same). These use DocuPilot's <code>{"{{#if}}"}</code> syntax so the discount / fee rows and conditions only render when present.</p>
          <div className="flex flex-wrap gap-3">
            <a className="underline text-primary" href="/downloads/Payout%20Letter%20-%20UK%20Redemption%20-%20DocuPilot.docx" download data-testid="payout-template-uk">UK — Loan Redemption Statement (111072)</a>
            <a className="underline text-primary" href="/downloads/Payout%20Letter%20-%20Australia%20-%20DocuPilot.docx" download data-testid="payout-template-aus">Australia — Loan Payout Letter (111071)</a>
          </div>
        </div>
        {!s ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="grid md:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}><Label className="text-xs">{label}</Label><Input type={type} value={s[k] ?? ""} onChange={(e) => setS((p) => ({ ...p, [k]: e.target.value }))} data-testid={`payout-setting-${k}`} /></div>
            ))}
          </div>
        )}
        <Button size="sm" onClick={save} disabled={busy || !s} data-testid="payout-settings-save">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save</Button>
      </CardContent>
    </Card>
  );
}
